import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/Reveal";
import { API } from "@/lib/api";
import { SERVICES, IMAGES } from "@/data/content";

const EMPTY = { name: "", email: "", company: "", service: "General enquiry", message: "" };

const inputCls =
  "w-full bg-white border border-ink/15 rounded-lg px-4 py-3 text-ink placeholder:text-ink/35 focus:outline-none focus:ring-2 focus:ring-cobalt/50 focus:border-cobalt transition-colors duration-200";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received", {
        description: "Thank you — a partner will respond within one business day.",
      });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send enquiry", {
        description: "Please check your details and try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <header className="pt-40 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="flex items-center gap-4 text-xs uppercase tracking-[0.35em] text-cobalt font-medium">
              <span className="h-px w-10 bg-cobalt/60" /> Contact
            </p>
          </Reveal>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.08]" data-testid="contact-heading">
            <MaskedLine delay={0.1}>Tell us the</MaskedLine>
            <MaskedLine delay={0.25}>
              <em className="text-cobalt">hard problem</em>.
            </MaskedLine>
          </h1>
        </div>
      </header>

      <section className="border-t border-ink/10 py-20 md:py-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-5 space-y-10">
            <Reveal>
              <p className="text-ink/60 text-base md:text-lg leading-relaxed max-w-md">
                One conversation is usually enough to know whether we can help — and if we
                can&rsquo;t, we&rsquo;ll tell you who can.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <Mail size={18} className="mt-1 text-cobalt" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-ink/40">Email</p>
                    <a href="mailto:help@axiomcomputers.com" data-testid="contact-email-link" className="text-ink hover:text-cobalt transition-colors duration-300">
                      help@axiomcomputers.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone size={18} className="mt-1 text-cobalt" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-ink/40">Phone</p>
                    <p className="text-ink">+91 79066 02341</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={18} className="mt-1 text-cobalt" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-ink/40">Headquarters</p>
                    <p className="text-ink">Meerut, Delhi (NCR), India</p>
                  </div>
                </li>
              </ul>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="relative overflow-hidden border border-ink/10 aspect-[16/9] group hidden md:block">
                <img
                  src={IMAGES.office}
                  alt="Axiom office environment"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  data-testid="contact-office-image"
                />
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="bg-white border border-ink/10 p-8 md:p-10 space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.25em] text-ink/50 mb-2">Name *</label>
                    <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Ada Lovelace" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.25em] text-ink/50 mb-2">Email *</label>
                    <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="ada@company.com" className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-company" className="block text-xs uppercase tracking-[0.25em] text-ink/50 mb-2">Company</label>
                    <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Analytical Engines Ltd" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-service" className="block text-xs uppercase tracking-[0.25em] text-ink/50 mb-2">Area of interest</label>
                    <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                      <option>General enquiry</option>
                      {SERVICES.map((s) => (
                        <option key={s.num}>{s.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.25em] text-ink/50 mb-2">The problem *</label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What are you trying to decide, predict, or build?"
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-3 rounded-full bg-ink text-paper px-8 py-4 text-sm tracking-wide transition-all duration-200 hover:bg-cobalt hover:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
                >
                  {sending ? "Sending…" : "Send enquiry"} <ArrowRight size={16} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
